import * as React from 'react';
import { connect } from 'react-redux';
import { Button, Col, Row } from 'reactstrap';
import ReactTable from 'react-table';
import Papa from 'papaparse';
import { toastr } from 'react-redux-toastr';
import { BeatLoader } from 'react-spinners';
import { ExportToCsv } from 'export-to-csv';
import SidebarContainer from 'src/modules/Routing/Sidebar/SidebarContainer';
import { IReduxState } from 'src/reduxTypes';
import { IAuthState } from 'src/modules/Auth/store/types';
import Columns from './components/Columns';
import ConfirmationModal from 'src/share/components/ConfirmationModal/ConfirmationModal';
import { CasesUploadService } from 'src/share/services/casesUpload';
import { ICaseRow, columnNames } from 'src/share/types/case/caseUpload';
import 'react-table/react-table.css';
import 'perfect-scrollbar-react/dist/style.min.css';
import './uploadCases.css';
import { CustomTbodyComponent } from 'src/share/components/BodyTable/BodyTable';
import { RoutesModules } from 'src/modules/Routing/types';

const initialCasesLists = {
  casesRows: [], casesRowsInvalid: [], casesList: [], casesError: []
}

export interface IUploadCasesProps {
  auth: IAuthState;
}

export interface IUploadCasesState {
  casesRows: ICaseRow[];
  casesRowsInvalid: ICaseRow[];
  casesList: any[];
  casesError: string[];
  showConfirmation: boolean;
  loading: boolean;
}

export class UploadCases extends React.Component<IUploadCasesProps, IUploadCasesState> {
  private fileInput;
  constructor(props: IUploadCasesProps) {
    super(props);
    this.state = {
      ...initialCasesLists,
      showConfirmation: false,
      loading: false
    }
  }

  public checkCasesRowsAgencies(casesRows: ICaseRow[]) {
    const isAdmin = this.props.auth.agencyCode === 'ALL';
    const casesErrorList = casesRows.reduce((errorList, { agencyCode, caseId }) => {
      if (isAdmin && this.doesTheAgencyExists(agencyCode) || !isAdmin && agencyCode !== this.props.auth.agencyCode) {
        errorList.push(this.getCaseId(caseId, agencyCode));
      }
      return errorList;
    }, [] as string[]);
    return [...new Set(casesErrorList)];
  }

  public doesTheAgencyExists(agencyCode: string) {
    return this.props.auth.agencies.find(agency => agency.code === agencyCode) === undefined;
  }

  public removeErrorCasesRows(casesRows: ICaseRow[], casesError: string[]) {
    return casesRows.filter(caseRow => !casesError.find(caseError => caseError === this.getCaseId(caseRow.caseId, caseRow.agencyCode)));
  }

  public getInvalidCasesRows(casesRows: ICaseRow[], casesError: string[]) {
    return casesRows.filter(caseRow => casesError.find(caseError => caseError === this.getCaseId(caseRow.caseId, caseRow.agencyCode)));
  }

  public fileHandler = (event) => {
    try {
      this.setState({ loading: true });
      event.preventDefault();
      const fileObject = event.target.files[0];
      if (fileObject) {
        Papa.parse(fileObject, { header: false, delimiter: ",", complete: this.processExcelResults });
      }
      event.value = '';
    } catch {
      toastr.error('Error', 'There was a problem loading the file');
    }
    this.setState({ loading: false });
  }
  public processExcelResults = (results, file) => {
    this.cleanUploadCases();
    if (!this.validColumns(results.data[0])) {
      toastr.error('Error', 'The file has invalid columns');
      return;
    }
    const casesRows = this.parseCasesRows(results.data);
    let casesError = this.checkCasesRowsAgencies(casesRows);
    const newCasesRowList = this.removeErrorCasesRows(casesRows, casesError);
    const { casesList, casesError: casesFormatError } = CasesUploadService.getCases(newCasesRowList, this.props.auth.agencies);
    casesError = casesError.concat(...casesFormatError);
    const casesRowsInvalid = this.getInvalidCasesRows(casesRows, casesError);
    this.setState({
      casesList,
      casesRows: this.removeErrorCasesRows(casesRows, casesError),
      casesRowsInvalid,
      casesError
    });
  }

  public getMaxMiscellaneousImages = (casesRows: ICaseRow[]) => {
    return casesRows.reduce((maxNumber, row) => {
      const miscellaneousLength = row.miscellaneous ? row.miscellaneous.split(',').length : 0;
      if (miscellaneousLength > maxNumber) {
        return miscellaneousLength;
      }
      return maxNumber;
    }, 0);
  }

  public uploadCases = async () => {
    try {
      this.setState({ loading: true });
      await CasesUploadService.uploadCases(this.state.casesList);
      this.toggle();
      this.setState({ loading: false });
      toastr.success('Case', `The ${this.state.casesRows.length > 0 ? 'cases are' : 'case is'} uploading`);
    } catch {
      toastr.error('Error', 'There was an error uploading the cases');
      this.setState({ loading: false });
    }
  }

  public parseCasesRows(rows: any[]): ICaseRow[] {
    rows.shift();
    rows = rows.filter(row => row.length > 0 && row[0]);
    return rows.reduce((casesRows, row) => {
      const caseRow = columnNames.reduce((caseData, column, index) => {
        caseData[column] = row[index] || '';
        return caseData;
      }, {});
      return [...casesRows, caseRow as ICaseRow];
    }, [] as ICaseRow[]);
  }

  public validColumns(columns: any[]) {
    return columnNames.every((column, index) => column === columns[index]);
  }

  public toggle() {
    this.setState({ showConfirmation: !this.state.showConfirmation })
  }

  public checkValidRow(caseId: string, agencyCode: string) {
    return this.state.casesError.find(errorCaseId => this.getCaseId(caseId, agencyCode) === `${errorCaseId}`);
  }

  public getCaseId(caseId: string, agencyCode: string) {
    const caseIdCleanedAgencyCode = caseId.toString().replace(agencyCode, '');
    return `${agencyCode}-${caseIdCleanedAgencyCode}`;
  }

  public downloadInvalidRows() {
    const options = {
      fieldSeparator: ',',
      quoteStrings: '"',
      showTitle: false,
      showLabels: true,
      useKeysAsHeaders: false,
      headers: columnNames
    };
    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(this.state.casesRowsInvalid);
  }

  public cleanUploadCases() {
    this.fileInput.value = '';
    this.setState({ ...initialCasesLists, loading: false })
  }

  public render() {
    const maxMiscellaneousImages = this.getMaxMiscellaneousImages(this.state.casesRows);
    const maxMiscellaneousImagesInvalid = this.getMaxMiscellaneousImages(this.state.casesRowsInvalid);
    return (
      <div className="upload-cases">
        <div className="upload-cases-content white-background">
          <Row>
            <Col className="col-md-12">
              <h3 className="title">Upload Cases</h3>
            </Col>
          </Row>
          <Row>
            <Col className="col-md-12">
              <input className="input-select" id="upload-files-input" type="file" onChange={this.fileHandler} accept=".xlsx, .xls, .csv" ref={reference => this.fileInput = reference} />
              <label htmlFor="upload-files-input" className="label-test btn-help">Select File</label>
              <Button className="btn btn-grey float-right btn-help"
                onClick={() => window.open(CasesUploadService.getExcelTemplateURL())}>
                Cases Template
              </Button>
            </Col>
          </Row>
          {this.state.casesRows.length > 0 &&
            <Col className="col-md-12 cases-table">
              <Row>
                <h4 className="title">Valid cases</h4>
                <ReactTable
                  showPagination={false}
                  loading={this.state.loading}
                  TbodyComponent={CustomTbodyComponent}
                  data={this.state.casesRows}
                  columns={Columns(maxMiscellaneousImages)}
                  defaultPageSize={this.state.casesRows.length}
                  className="-striped -highlight"
                />
              </Row>
            </Col>}
          {this.state.casesRows.length > 0 && this.state.casesRowsInvalid.length !== 0 &&
            <Row>
              <Col className="col-md-12">
                <Button className="btn btn-blue float-right btn-help" onClick={() => this.toggle()}>
                  Upload Valid
                </Button>
              </Col>
            </Row>}
          {this.state.casesRowsInvalid.length > 0 &&
            <Col className="col-md-12 cases-table">
              <Row>
                <h4 className="title">Invalid cases</h4>
                <ReactTable
                  showPagination={false}
                  loading={this.state.loading}
                  TbodyComponent={CustomTbodyComponent}
                  data={this.state.casesRowsInvalid}
                  columns={Columns(maxMiscellaneousImagesInvalid)}
                  defaultPageSize={this.state.casesRowsInvalid.length}
                  className="-striped -highlight"
                />
              </Row>
            </Col>}
          {(this.state.casesRows.length > 0 || this.state.casesRowsInvalid.length > 0) &&
            <Row>
              <Col className="col-md-12">
                {this.state.casesRowsInvalid.length > 0 ?
                  <Button className="btn btn-blue float-right btn-help button-download-csv" onClick={() => this.downloadInvalidRows()}>
                    Download Invalid
                  </Button> :
                  <Button className="btn btn-blue float-right btn-help btn-upload" onClick={() => this.toggle()}>
                    Upload Valid
                  </Button>}
                <Button className="btn btn-grey float-right btn-help" onClick={() => this.cleanUploadCases()}>
                  Clean
              </Button>
              </Col>
            </Row>}
          <ConfirmationModal
            isOpen={this.state.showConfirmation}
            toggle={() => this.toggle()}
            title={'Upload cases'}
            description={this.state.loading ? 'Uploading...' : `Do you want to upload ${this.state.casesRows.length > 0 ? 'these cases' : 'this case'}?`}
            onContinue={() => this.uploadCases()}
          />
          <div className="help-loader">
            <BeatLoader loading={this.state.loading} />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxState) => ({
  auth: state.auth
});

export default SidebarContainer(RoutesModules.SETTINGS)(connect(mapStateToProps, null)(UploadCases));
